import type { Area } from '../data/financialData';

interface ExportOptions {
  area: Area | string;
  branch: string;
  startIdx: number;
  endIdx: number;
  sectionName: string;
  activeEntity: string;
  activeData: any; // Dynamic active entity data to fix profile sync bug
  domTables?: { title: string; headers: string[]; rows: string[][] }[]; // DOM-extracted tables
}

// Utility to apply minimalist styling and number formatting to the sheet
function formatPremiumSheet(ws: any) {
  // Set custom wide columns for a more spacious, premium feel
  ws['!cols'] = [
    { wch: 24 }, // Column A
    { wch: 24 }, // Column B
    { wch: 22 }, // Column C
    { wch: 22 }, // Column D
    { wch: 22 }, // Column E
    { wch: 22 }, // Column F
    { wch: 22 }, // Column G
  ];

  // Iterate over all cells to apply number formats
  const range = (window as any).XLSX?.utils?.decode_range(ws['!ref']) || { s: { c: 0, r: 0 }, e: { c: 6, r: 150 } };
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = { c: C, r: R };
      const cell_ref = (window as any).XLSX?.utils?.encode_cell(cell_address);
      if (!cell_ref) continue;
      const cell = ws[cell_ref];
      
      if (!cell) continue;

      // If it's a number and seems like a currency (large value or monetary field)
      if (cell.t === 'n') {
        // Apply clean Indonesian Rupiah accounting format
        cell.z = '"Rp" #,##0.00;[Red]"Rp" -#,##0.00;"Rp" -';
      }
    }
  }
}

export function exportDashboardToExcel(opts: ExportOptions) {
  const XLSX = require('xlsx');
  (window as any).XLSX = XLSX;
  const wb = XLSX.utils.book_new();

  const { area, branch, startIdx, endIdx, sectionName, activeData, domTables } = opts;
  
  // Destructure functions from activeData (fixing the Dual-Track data mismatch bug!)
  const {
    MONTHS, getRevenueExpensesSeries, getExpenseBreakdown,
    getCashFlowSeries, getPayablesReceivables, getRevenueByBranch,
    getExpensesByBranch, getKPIs
  } = activeData;

  const effectiveKey = branch === 'ALL' ? (area as string) : branch;
  const monthRange = `${MONTHS[startIdx].label} – ${MONTHS[endIdx].label}`;
  const title = `JGJB Finance — ${area}${branch !== 'ALL' ? ` / ${branch}` : ''}`;
  const generated = `Generated on: ${new Date().toLocaleDateString('id-ID')} | Premium Report`;

  // --- Dynamic Screen-Extracted Tables Integration ---
  if (domTables && domTables.length > 0) {
    domTables.forEach((table, index) => {
      // Clean sheet name (Excel limits to 31 chars and bans special chars: \ / ? * : [ ])
      // eslint-disable-next-line no-useless-escape
      let sheetName = table.title
        .replace(/[\[\]\?\*\/\\:]/g, '')
        .trim();
      
      if (sheetName.length > 30) {
        sheetName = sheetName.substring(0, 27) + '...';
      }
      
      if (!sheetName) {
        sheetName = `Sheet ${index + 1}`;
      }

      const aoaData: any[][] = [
        [title],
        [`${table.title} — ${monthRange}`],
        [generated],
        [],
        table.headers,
        ...table.rows.map(row => row.map((cell, cIdx) => {
          if (!cell) return '';

          // 1. Check if it's Rupiah currency
          if (cell.includes('Rp') || cell.includes('IDR')) {
            const multiplier = cell.includes('-') ? -1 : 1;
            const clean = parseFloat(cell.replace(/[^0-9]/g, ''));
            return isNaN(clean) ? cell : (clean * multiplier);
          }

          // 2. Check if it's a percentage
          if (cell.endsWith('%')) {
            const isNegative = cell.includes('-');
            const multiplier = isNegative ? -1 : 1;
            const clean = parseFloat(cell.replace(/[^0-9.]/g, ''));
            return isNaN(clean) ? cell : (clean / 100 * multiplier);
          }

          // 3. General numeric parsing (accounting for Indonesia dots/commas format if present)
          const cleanCell = cell.replace(/\./g, '').replace(',', '.'); // Remove thousand dots, change decimal comma
          const cleanNum = parseFloat(cleanCell);
          
          // Verify if the string was pure numeric before replacing characters
          // eslint-disable-next-line no-useless-escape
          const numericCheck = cell.replace(/[,. \-]/g, '');
          if (numericCheck && !isNaN(cleanNum) && /^\d+$/.test(numericCheck)) {
            const isNegative = cell.includes('-');
            return cleanNum * (isNegative ? -1 : 1);
          }

          return cell;
        }))
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoaData);
      formatPremiumSheet(ws);

      // Apply specific formats for percentages or custom fields
      const range = XLSX.utils.decode_range(ws['!ref']);
      table.headers.forEach((h, cIdx) => {
        if (h.toLowerCase().includes('%') || h.toLowerCase().includes('variance (%)') || h.toLowerCase().includes('margin') || h.toLowerCase().includes('growth') || h.toLowerCase().includes('ratio')) {
          for (let R = 5; R <= range.e.r; ++R) {
            const cell_ref = XLSX.utils.encode_cell({ c: cIdx, r: R });
            const cell = ws[cell_ref];
            if (cell && cell.t === 'n') {
              if (h.toLowerCase().includes('ratio')) {
                cell.z = '0.00"x"';
              } else {
                cell.z = '0.00%';
              }
            }
          }
        }
      });

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
  }

  // --- Fallback & Consolidated Analytical Sheets ---
  // Ensure we still append consolidated P&L summaries based on the active tab if dynamic sheets are not enough
  // eslint-disable-next-line no-useless-escape
  const addedSheets = new Set(domTables?.map(t => t.title.replace(/[\[\]\?\*\/\\:]/g, '').trim().substring(0, 30)) || []);

  if (sectionName === 'dashboard' && !addedSheets.has('Dashboard')) {
    const kpis = getKPIs(effectiveKey, startIdx, endIdx);
    const revExp = getRevenueExpensesSeries(effectiveKey, startIdx, endIdx);
    const cf = getCashFlowSeries(effectiveKey, startIdx, endIdx);
    const apar = getPayablesReceivables(effectiveKey, endIdx);

    const dashData: any[][] = [
      [title],
      [monthRange],
      [generated],
      [],
      ['SUMMARY KPI'],
      ['Metric', 'Value'],
      ['Total Revenue', kpis.totalRevenue],
      ['Total OpEx', kpis.opex],
      ['Operating Profit', kpis.opProfit],
      ['Net Profit', kpis.netProfit],
      ['Net Cash from Operations', kpis.netCashFromOps],
      [],
      ['MONTHLY BREAKDOWN'],
      ['Month', 'Revenue', 'OpEx', 'Operating Profit', 'Net Cash Ops'],
      ...revExp.labels.map((label: any, idx: number) => [
        label,
        revExp.revenue[idx],
        revExp.opex[idx],
        revExp.revenue[idx] - revExp.opex[idx],
        cf.operating[idx],
      ]),
      [],
      ['ACCOUNTS PAYABLE & RECEIVABLE'],
      ['Category', 'Amount'],
      ['Total Payables', apar.payable.total],
      ['Total Receivables', apar.receivable.total],
      ['Net AR', apar.receivable.total - apar.payable.total],
    ];

    const ws = XLSX.utils.aoa_to_sheet(dashData);
    formatPremiumSheet(ws);
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidated P&L');
  }

  if (sectionName === 'revenue' && !addedSheets.has('Revenue')) {
    const revExp = getRevenueExpensesSeries(effectiveKey, startIdx, endIdx);
    const revByBranch = getRevenueByBranch(startIdx, endIdx);

    const revData: any[][] = [
      [title],
      [`Revenue Analysis — ${monthRange}`],
      [generated],
      [],
      ['MONTHLY REVENUE'],
      ['Month', 'Revenue', 'OpEx', 'Net Profit'],
      ...revExp.labels.map((label: any, idx: number) => [
        label,
        revExp.revenue[idx],
        revExp.opex[idx],
        revExp.revenue[idx] - revExp.opex[idx],
      ]),
      [],
      ['REVENUE BY BRANCH'],
      ['Branch Code', 'Area', 'Total Revenue', 'Avg Per Month', '% of Area'],
      ...revByBranch.map((item: any) => [
        item.branch,
        item.area,
        item.totalPeriod,
        item.avgPerMonth,
        item.pctOfArea / 100, // as percentage
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(revData);
    formatPremiumSheet(ws);
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 11; R <= range.e.r; ++R) {
      const cell_ref = XLSX.utils.encode_cell({ c: 4, r: R });
      if (ws[cell_ref] && ws[cell_ref].t === 'n') {
        ws[cell_ref].z = '0.00%';
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidated Revenue');
  }

  if (sectionName === 'expenses' && !addedSheets.has('Expenses')) {
    const revExp = getRevenueExpensesSeries(effectiveKey, startIdx, endIdx);
    const breakdown = getExpenseBreakdown(effectiveKey, startIdx, endIdx);
    const expByBranch = getExpensesByBranch(startIdx, endIdx);

    const expData: any[][] = [
      [title],
      [`Expenses Analysis — ${monthRange}`],
      [generated],
      [],
      ['MONTHLY EXPENSES'],
      ['Month', 'OpEx'],
      ...revExp.labels.map((label: any, idx: number) => [
        label,
        revExp.opex[idx],
      ]),
      [],
      ['EXPENSE BREAKDOWN'],
      ['Category', 'Amount', '% of Total'],
      ...breakdown.map((item: any) => [
        item.category,
        item.value,
        (item.value / (breakdown.reduce((sum: number, c: any) => sum + c.value, 0) || 1)),
      ]),
      [],
      ['EXPENSES BY BRANCH'],
      ['Branch Code', 'Area', 'Total Expenses', 'Avg Per Month', '% of Area'],
      ...expByBranch.map((item: any) => [
        item.branch,
        item.area,
        item.totalPeriod,
        item.avgPerMonth,
        item.pctOfArea / 100,
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(expData);
    formatPremiumSheet(ws);
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 11; R <= range.e.r; ++R) {
      const cell_ref = XLSX.utils.encode_cell({ c: 2, r: R });
      if (ws[cell_ref] && ws[cell_ref].t === 'n') {
        ws[cell_ref].z = '0.00%';
      }
    }
    for (let R = 15; R <= range.e.r; ++R) {
      const cell_ref = XLSX.utils.encode_cell({ c: 4, r: R });
      if (ws[cell_ref] && ws[cell_ref].t === 'n') {
        ws[cell_ref].z = '0.00%';
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidated Expenses');
  }

  const filename = `JGJB_${area}${branch !== 'ALL' ? `_${branch}` : ''}_${sectionName}_${MONTHS[startIdx].month}_to_${MONTHS[endIdx].month}.xlsx`;
  XLSX.writeFile(wb, filename);
}
